import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsent, CONSENT_CATEGORIES } from '../contexts/ConsentContext';
import { X, Cookie, Shield, BarChart3, Megaphone, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

// Cookie category descriptions
const CATEGORY_INFO = {
  necessary: {
    icon: Shield,
    titleKey: 'cookies.necessary.title',
    descKey: 'cookies.necessary.desc',
    defaultTitle: 'Necessary',
    defaultDesc: 'Essential cookies required for the website to function. Cannot be disabled.',
    required: true,
  },
  analytics: {
    icon: BarChart3,
    titleKey: 'cookies.analytics.title',
    descKey: 'cookies.analytics.desc',
    defaultTitle: 'Analytics',
    defaultDesc: 'Help us understand how visitors interact with our website to improve user experience.',
    required: false,
  },
  marketing: {
    icon: Megaphone,
    titleKey: 'cookies.marketing.title',
    descKey: 'cookies.marketing.desc',
    defaultTitle: 'Marketing',
    defaultDesc: 'Used to deliver personalized advertisements and measure ad campaign effectiveness.',
    required: false,
  },
  preferences: {
    icon: Settings,
    titleKey: 'cookies.preferences.title',
    descKey: 'cookies.preferences.desc',
    defaultTitle: 'Preferences',
    defaultDesc: 'Remember your preferences like language and region for a better experience.',
    required: false,
  },
};

// Main Cookie Banner Component
const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const { showBanner, acceptAll, rejectAll, openPreferences } = useConsent();

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom-4 duration-300">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex p-3 bg-primary/10 rounded-xl shrink-0">
              <Cookie className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {t('cookies.banner.title', 'We Value Your Privacy')}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t('cookies.banner.description', 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies. You can manage your preferences at any time.')}
              </p>
              
              <div className="flex flex-wrap gap-3 mt-4">
                <Button onClick={acceptAll} className="flex-1 sm:flex-none">
                  <Check className="h-4 w-4 mr-2" />
                  {t('cookies.banner.acceptAll', 'Accept All')}
                </Button>
                <Button onClick={rejectAll} variant="outline" className="flex-1 sm:flex-none">
                  {t('cookies.banner.rejectAll', 'Reject All')}
                </Button>
                <Button onClick={openPreferences} variant="ghost" className="flex-1 sm:flex-none text-primary">
                  <Settings className="h-4 w-4 mr-2" />
                  {t('cookies.banner.customize', 'Customize')}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Privacy Policy Link */}
        <div className="px-5 sm:px-6 py-3 bg-slate-50 border-t border-slate-100">
          <p className="text-xs text-slate-500">
            {t('cookies.banner.learnMore', 'Learn more in our')}{' '}
            <a href="/privacy-policy" className="text-primary hover:underline">
              {t('cookies.banner.privacyPolicy', 'Privacy Policy')}
            </a>
            {' '}{t('cookies.banner.and', 'and')}{' '}
            <a href="/terms-of-service" className="text-primary hover:underline">
              {t('cookies.banner.cookiePolicy', 'Cookie Policy')}
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
};

// Preferences Modal Component
const CookiePreferencesModal = () => {
  const { t } = useTranslation();
  const { showPreferences, closePreferences, savePreferences, consent } = useConsent();
  
  const [preferences, setPreferences] = useState({
    analytics: consent.analytics,
    marketing: consent.marketing,
    preferences: consent.preferences,
  });

  // Update local state when consent changes
  React.useEffect(() => {
    setPreferences({
      analytics: consent.analytics,
      marketing: consent.marketing,
      preferences: consent.preferences,
    });
  }, [consent]);

  if (!showPreferences) return null;

  const handleToggle = (category) => {
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    savePreferences(preferences);
  };

  const handleAcceptAll = () => {
    savePreferences({
      analytics: true,
      marketing: true,
      preferences: true,
    });
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Cookie className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              {t('cookies.preferences.title', 'Cookie Preferences')}
            </h2>
          </div>
          <button
            onClick={closePreferences}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          <p className="text-sm text-slate-600 mb-5">
            {t('cookies.preferences.description', 'Choose which cookies you want to accept. Your preferences will be saved and you can change them at any time.')}
          </p>

          <div className="space-y-4">
            {Object.entries(CATEGORY_INFO).map(([key, info]) => {
              const Icon = info.icon;
              const isRequired = info.required;
              const isEnabled = key === 'necessary' ? true : preferences[key];

              return (
                <div
                  key={key}
                  className={`p-4 rounded-lg border ${
                    isEnabled ? 'border-primary/30 bg-primary/5' : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isEnabled ? 'bg-primary/20' : 'bg-slate-200'}`}>
                        <Icon className={`h-4 w-4 ${isEnabled ? 'text-primary' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">
                          {t(info.titleKey, info.defaultTitle)}
                          {isRequired && (
                            <span className="ml-2 text-xs font-normal text-slate-500">
                              ({t('cookies.required', 'Required')})
                            </span>
                          )}
                        </h4>
                        <p className="text-sm text-slate-600 mt-1">
                          {t(info.descKey, info.defaultDesc)}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => !isRequired && handleToggle(key)}
                      disabled={isRequired}
                      className="shrink-0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap gap-3 p-5 border-t border-slate-200 bg-slate-50">
          <Button onClick={handleSave} className="flex-1">
            {t('cookies.preferences.savePreferences', 'Save Preferences')}
          </Button>
          <Button onClick={handleAcceptAll} variant="outline" className="flex-1">
            {t('cookies.preferences.acceptAll', 'Accept All')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Export - includes both Banner and Modal
const CookieConsent = () => {
  return (
    <>
      <CookieConsentBanner />
      <CookiePreferencesModal />
    </>
  );
};

// Export components separately for flexibility
export { CookieConsentBanner, CookiePreferencesModal };
export default CookieConsent;
